/**
 * Single source of truth for Billx route metadata: pageKey, web paths, and mobile in-app paths.
 * Presentation (icons, layout) stays in each app; this module is data-only.
 */

/** Web path prefix (no trailing slash) */
export const WEB_DASHBOARD_PREFIX = '/dashboard';
export const WEB_STAFF_PREFIX = '/staff';

export type BusinessDashboardTabDef = {
  /** File name under the business dashboard group (e.g. index → overview) */
  screenName: string;
  pageKey: string;
  title: string;
  /** Absolute web path */
  webPath: string;
  /** In-app path for the mobile shell */
  mobileAppPath: string;
};

/** Order matches the business dashboard tab bar */
export const BUSINESS_DASHBOARD_TAB_DEFS: readonly BusinessDashboardTabDef[] = [
  {
    screenName: 'index',
    pageKey: 'dashboard.home',
    title: 'Overview',
    webPath: '/dashboard/home',
    mobileAppPath: '/dashboard',
  },
  {
    screenName: 'menu',
    pageKey: 'dashboard.outlet.menu',
    title: 'Menu',
    webPath: '/dashboard/outlet/menu',
    mobileAppPath: '/dashboard/menu',
  },
  {
    screenName: 'tables',
    pageKey: 'dashboard.outlet.tables',
    title: 'Tables',
    webPath: '/dashboard/outlet/tables',
    mobileAppPath: '/dashboard/tables',
  },
  {
    screenName: 'orders',
    pageKey: 'dashboard.orders',
    title: 'Orders',
    webPath: '/dashboard/orders',
    mobileAppPath: '/dashboard/orders',
  },
  {
    screenName: 'users',
    pageKey: 'dashboard.users',
    title: 'Users',
    webPath: '/dashboard/users',
    mobileAppPath: '/dashboard/users',
  },
  {
    screenName: 'customers',
    pageKey: 'dashboard.customers',
    title: 'Customers',
    webPath: '/dashboard/customers',
    mobileAppPath: '/dashboard/customers',
  },
  {
    screenName: 'analytics',
    pageKey: 'dashboard.analytics',
    title: 'Analytics',
    webPath: '/dashboard/analytics',
    mobileAppPath: '/dashboard/analytics',
  },
  {
    screenName: 'settings',
    pageKey: 'dashboard.settings',
    title: 'Settings',
    webPath: '/dashboard/settings',
    mobileAppPath: '/dashboard/settings',
  },
];

export type StaffAppTabDef = {
  screenName: string;
  pageKey: string;
  title: string;
  webPath: string;
  mobileAppPath: string;
};

/** Mobile tab order; web sidebar order is applied in useStaffNavItems */
export const STAFF_APP_TAB_DEFS: readonly StaffAppTabDef[] = [
  {
    screenName: 'index',
    pageKey: 'staff.home',
    title: 'Home',
    webPath: '/staff/home',
    mobileAppPath: '/staff',
  },
  {
    screenName: 'kitchen',
    pageKey: 'staff.kitchen',
    title: 'Kitchen',
    webPath: '/staff/kitchen',
    mobileAppPath: '/staff/kitchen',
  },
  {
    screenName: 'waiter',
    pageKey: 'staff.waiter',
    title: 'Waiter',
    webPath: '/staff/waiter',
    mobileAppPath: '/staff/waiter',
  },
  {
    screenName: 'billing',
    pageKey: 'staff.billing',
    title: 'Billing',
    webPath: '/staff/billing',
    mobileAppPath: '/staff/billing',
  },
  {
    screenName: 'delivery',
    pageKey: 'staff.delivery',
    title: 'Delivery',
    webPath: '/staff/delivery',
    mobileAppPath: '/staff/delivery',
  },
  {
    screenName: 'settings',
    pageKey: 'user',
    title: 'Settings',
    /** Distinct from web `/user` (profile) so deep-link resolution stays unambiguous */
    webPath: '/staff/settings',
    mobileAppPath: '/staff/settings',
  },
];

export type DashboardSettingsRowDef = {
  id: string;
  pageKey: string;
  label: string;
  description: string;
  webPath: string;
  mobileAppPath: string;
};

/** Grouping for the mobile settings hub (titles + row ids) */
export const DASHBOARD_SETTINGS_UI_SECTIONS: readonly {
  title: string;
  ids: readonly string[];
}[] = [
  { title: 'Account', ids: ['profile'] },
  { title: 'Business', ids: ['business', 'outlets', 'team', 'subscriptions'] },
  { title: 'Operations', ids: ['ordering', 'tax', 'notifications'] },
];

export const DASHBOARD_SETTINGS_ROW_DEFS: readonly DashboardSettingsRowDef[] = [
  {
    id: 'profile',
    pageKey: 'user',
    label: 'Profile',
    description: 'Display name and account details',
    webPath: '/user',
    mobileAppPath: '/dashboard/profile',
  },
  {
    id: 'business',
    pageKey: 'dashboard.settings.business',
    label: 'Business Info',
    description: 'Name, address, GSTIN, contact',
    webPath: '/dashboard/settings/business',
    mobileAppPath: '/dashboard/settings/business',
  },
  {
    id: 'outlets',
    pageKey: 'dashboard.settings.outlets',
    label: 'Outlets',
    description: 'Manage restaurant outlets',
    webPath: '/dashboard/settings/outlets',
    mobileAppPath: '/dashboard/settings/outlets',
  },
  {
    id: 'team',
    pageKey: 'dashboard.users',
    label: 'Team Members',
    description: 'Invite and manage staff',
    webPath: '/dashboard/users',
    mobileAppPath: '/dashboard/users',
  },
  {
    id: 'subscriptions',
    pageKey: 'dashboard.settings.subscriptions',
    label: 'Subscription',
    description: 'Manage billing and plans',
    webPath: '/dashboard/settings/subscriptions',
    mobileAppPath: '/dashboard/settings/subscriptions',
  },
  {
    id: 'ordering',
    pageKey: 'dashboard.settings.ordering',
    label: 'Ordering',
    description: 'Order types, self-ordering, display',
    webPath: '/dashboard/settings/ordering',
    mobileAppPath: '/dashboard/settings/ordering',
  },
  {
    id: 'tax',
    pageKey: 'dashboard.settings.tax',
    label: 'Tax & Billing',
    description: 'SGST, CGST, currency',
    webPath: '/dashboard/settings/tax',
    mobileAppPath: '/dashboard/settings/tax',
  },
  {
    id: 'notifications',
    pageKey: 'dashboard.settings.notifications',
    label: 'Notifications',
    description: 'Alerts and sound settings',
    webPath: '/dashboard/settings/notifications',
    mobileAppPath: '/dashboard/settings/notifications',
  },
];

/** Web sidebar: top-level ids and nested outlet children (same pageKeys as mobile flat tabs) */
export const WEB_DASHBOARD_NAV_TREE: readonly (
  | {
      kind: 'item';
      id: string;
      pageKey: string;
      label: string;
      webPath: string;
    }
  | {
      kind: 'group';
      id: string;
      pageKey: string;
      label: string;
      children: readonly {
        id: string;
        pageKey: string;
        label: string;
        webPath: string;
      }[];
    }
)[] = [
  {
    kind: 'item',
    id: 'home',
    pageKey: 'dashboard.home',
    label: 'Home',
    webPath: '/dashboard/home',
  },
  {
    kind: 'group',
    id: 'outlet',
    pageKey: 'dashboard.outlet',
    label: 'Outlet',
    children: [
      {
        id: 'menu',
        pageKey: 'dashboard.outlet.menu',
        label: 'Menu',
        webPath: '/dashboard/outlet/menu',
      },
      {
        id: 'tables',
        pageKey: 'dashboard.outlet.tables',
        label: 'Tables',
        webPath: '/dashboard/outlet/tables',
      },
    ],
  },
  {
    kind: 'item',
    id: 'orders',
    pageKey: 'dashboard.orders',
    label: 'Orders',
    webPath: '/dashboard/orders',
  },
  {
    kind: 'item',
    id: 'users',
    pageKey: 'dashboard.users',
    label: 'Users',
    webPath: '/dashboard/users',
  },
  {
    kind: 'item',
    id: 'customers',
    pageKey: 'dashboard.customers',
    label: 'Customers',
    webPath: '/dashboard/customers',
  },
  {
    kind: 'group',
    id: 'settings',
    pageKey: 'dashboard.settings',
    label: 'Settings',
    children: (() => {
      const labelOverrides: Record<string, string> = {
        business: 'Business Details',
        ordering: 'Ordering & Menu',
        tax: 'Billing & Taxes',
        subscriptions: 'Subscription & Quota',
      };

      return DASHBOARD_SETTINGS_ROW_DEFS.filter(
        (r) => r.id !== 'team' && r.id !== 'profile'
      ).map((r) => ({
        id: r.id,
        pageKey: r.pageKey,
        label: labelOverrides[r.id] || r.label,
        webPath: r.webPath,
      }));
    })(),
  },
];

/** Web staff nav order (matches previous useStaffNavItems order) */
export const WEB_STAFF_NAV_DEFS: readonly {
  id: string;
  pageKey: string;
  label: string;
  webPath: string;
}[] = [
  {
    id: 'home',
    pageKey: 'staff.home',
    label: 'Home',
    webPath: '/staff/home',
  },
  {
    id: 'billing',
    pageKey: 'staff.billing',
    label: 'Billing',
    webPath: '/staff/billing',
  },
  {
    id: 'waiter',
    pageKey: 'staff.waiter',
    label: 'Waiter',
    webPath: '/staff/waiter',
  },
  {
    id: 'kitchen',
    pageKey: 'staff.kitchen',
    label: 'Kitchen',
    webPath: '/staff/kitchen',
  },
  {
    id: 'delivery',
    pageKey: 'staff.delivery',
    label: 'Delivery',
    webPath: '/staff/delivery',
  },
];

function normalizeWebPath(path: string): string {
  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}

/**
 * Build map: normalized web path (no leading slash) → mobile in-app path (no query string).
 */
function buildExactDeepLinkMap(): Record<string, string> {
  const map: Record<string, string> = {};

  for (const def of BUSINESS_DASHBOARD_TAB_DEFS) {
    map[normalizeWebPath(def.webPath)] = def.mobileAppPath;
  }
  // Alias: /dashboard alone → same as home tab
  map['dashboard'] = '/dashboard';
  map['dashboard/home'] = '/dashboard';

  for (const def of STAFF_APP_TAB_DEFS) {
    map[normalizeWebPath(def.webPath)] = def.mobileAppPath;
  }

  for (const row of DASHBOARD_SETTINGS_ROW_DEFS) {
    map[normalizeWebPath(row.webPath)] = row.mobileAppPath;
  }

  return map;
}

const EXACT_DEEP_LINK_MAP = buildExactDeepLinkMap();

/**
 * Resolve mobile in-app path from an incoming web-style path (normalized, no query).
 * Returns undefined if no static mapping; callers may apply dynamic patterns (e.g. order id).
 */
export function mobileAppPathForWebPath(
  normalizedPath: string
): string | undefined {
  return EXACT_DEEP_LINK_MAP[normalizedPath];
}

/** All pageKeys declared in this registry (for tests and validation). */
export function collectRegistryPageKeys(): string[] {
  const keys = new Set<string>();

  for (const def of BUSINESS_DASHBOARD_TAB_DEFS) {
    keys.add(def.pageKey);
  }
  for (const def of STAFF_APP_TAB_DEFS) {
    keys.add(def.pageKey);
  }
  for (const row of DASHBOARD_SETTINGS_ROW_DEFS) {
    keys.add(row.pageKey);
  }

  const walkNav = (nodes: typeof WEB_DASHBOARD_NAV_TREE): void => {
    for (const node of nodes) {
      keys.add(node.pageKey);
      if (node.kind === 'group') {
        for (const c of node.children) {
          keys.add(c.pageKey);
        }
      }
    }
  };
  walkNav(WEB_DASHBOARD_NAV_TREE);

  for (const def of WEB_STAFF_NAV_DEFS) {
    keys.add(def.pageKey);
  }

  return [...keys];
}
